/* SUMMARY
  * Imports
  * Interfaces & Helpers
  * ColorSelector: Modern & intuitive color selector component matching react-select ISO
*/

/* Imports */
import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@heroicons/react/24/outline";
import { color } from "../../../services/colors/colors";
import "./ColorSelect.scss";
/***/

/* Interfaces & Helpers */
export interface ColorSelectorProps {
  value?: string;
  onChange?: (c: string) => void;
  disabled?: boolean;
  clearable?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Calculates luminance to provide high-contrast icon color (white or dark)
 */
function getContrastColor(hexColor: string): string {
  if (!hexColor || !hexColor.startsWith("#")) return "#ffffff";
  const hex = hexColor.replace("#", "");
  if (hex.length !== 6) return "#ffffff";
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // YIQ luminance formula
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 160 ? "#1e293b" : "#ffffff";
}
/***/

/**
 * ColorSelector: Modern color picker component matching react-select structure
 */
export default function ColorSelector({
  value,
  onChange,
  disabled = false,
  clearable = true,
  placeholder,
  className = ""
}: ColorSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const colorsList = useMemo(() => color.getAllColors(), []);
  const activeColorHex = value ? color.getColor(value, "500") : null;

  // Close palette on click outside or escape key
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleColorChoice = (c: string) => {
    if (disabled) return;
    setOpen(false);
    onChange?.(c);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange?.("");
  };

  const toggleOpen = () => {
    if (disabled) return;
    setOpen(!open);
  };

  return (
    <div className={`color-selector-wrapper ${className}`} ref={containerRef}>
      {/* Selector Trigger Input formatted ISO with react-select */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleOpen();
          }
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`react-select__control color-react-select-control ${open ? "react-select__control--is-focused" : ""} ${disabled ? "react-select__control--is-disabled" : ""}`}
      >
        <div className="react-select__value-container color-select-value-container">
          {activeColorHex ? (
            <div
              className="color-swatch-selected"
              style={{ backgroundColor: activeColorHex }}
            />
          ) : (
            <div className="react-select__placeholder text-gray-500">
              {placeholder || t("common:selectColor", "Sélectionner une couleur")}
            </div>
          )}
        </div>

        <div className="react-select__indicators">
          {clearable && value && !disabled && (
            <div
              role="button"
              tabIndex={0}
              title={t("common:actions.cancel", "Effacer")}
              className="react-select__indicator react-select__clear-indicator"
              onClick={handleClear}
              onKeyDown={(e) => e.key === "Enter" && handleClear(e as any)}
            >
              <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="css-tj5bde-Svg">
                <path fill="currentColor" d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
              </svg>
            </div>
          )}

          <span className="react-select__indicator-separator" />

          <div className={`react-select__indicator react-select__dropdown-indicator ${open ? "is-open" : ""}`}>
            <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="css-tj5bde-Svg">
              <path fill="currentColor" d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Floating Palette Dropdown matching react-select__menu */}
      {open && (
        <div className="react-select__menu color-palette-menu" role="listbox">
          <div className="palette-grid">
            {colorsList.map((c, index) => {
              const hexValue = color.getColor(c, "500");
              const isSelected = c === value;
              const contrastIconColor = getContrastColor(hexValue);

              return (
                <button
                  key={`color-${index}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  style={{ backgroundColor: hexValue }}
                  className={`color-swatch-item ${isSelected ? "selected" : ""}`}
                  onClick={() => handleColorChoice(c)}
                >
                  {isSelected && (
                    <CheckIcon
                      className="check-icon"
                      style={{ color: contrastIconColor }}
                      strokeWidth={3}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
/***/
